import React, { useState, useMemo } from "react";
import { Button } from "../../../../shadcn/components/ui/button";
import { Card } from "../../../../shadcn/components/ui/card";
import {
  BidInterestRequestType,
  useBidInterestApi,
} from "../../../../hoc/useBidInterestApi";
import { useMissingUserDetailsControls } from "../../../../hoc/useMissingUserDetailsControls";
import { PDAskUserMissingDetailsModal } from "./PDAskUserMissingDetailsModal";
import { PDViewAuthorisedOfficerPopupModal } from "./PDViewAuthorisedOfficerPopupModal";
import { PropertyType } from "./PropertyDetailWrapper";
import { useRequestContactDetailsApi } from "../../../../hoc/useRequestContactDetailsApi";
import { useAsyncAction } from "../../../../hoc/useAsyncAction";
import { useWithAuth } from "../../../../hoc/useWithAuth";

interface PDContactAuthorisedOfficerPropsInterface {
  propertyData: any;
  setAuthModelOpen: (value: boolean) => void;
  propertyType: PropertyType;
  title?: string;
}

export type RequestCallbackViaType = "Desktop" | "Mobile";

export const PDContactAuthorisedOfficer: React.FC<
  PDContactAuthorisedOfficerPropsInterface
> = ({
  propertyData,
  setAuthModelOpen,
  propertyType = "auction_property",
  title = "View Authorised Officer Details",
}) => {
  const [openContactDetailsModal, setOpenContactDetailsModal] = useState(false);

  const {
    missingFields,
    openMissingDetailsModal,
    setOpenMissingDetailsModal,
    askUserDetailsModalInitiatedFrom,
    setAskUserMissingDetailsModalInitiatedFrom,
    handleOpenCollectMissingDetailsPopup,
    missingDetails,
    setMissingDetails,
    resolvedName,
    resolvedEmail,
    resolvedMobile,
  } = useMissingUserDetailsControls();
  const [requestCallbackVia, setRequestCallbackVia] =
    useState<RequestCallbackViaType>("Desktop");

  const { withAuth } = useWithAuth({
    openAuthModel: () => setAuthModelOpen(true),
  });

  const propertyAuthorisedOfficerContactDetails = useMemo(() => {
    return {
      contact_names: propertyData?.contact_names,
      contact_mobiles: propertyData?.contact_mobiles,
      contact_emails: propertyData?.contact_emails,
      contact: propertyData?.contact,
    };
  }, [propertyData]);

  const { execute: triggerRequestContactDetailsApi } =
    useRequestContactDetailsApi();

  const { execute: triggerBidInterestApi } = useBidInterestApi();

  const {
    execute: handleRequestContactDetailsButtonClick,
    isLoading: isRequestContactDetailsButtonLoading,
    isSuccess: isRequestContactDetailsButtonSuccess,
    isError: isRequestContactDetailsButtonError,
    reset: resetHandleRequestContactDetailsButtonClick,
  } = useAsyncAction(
    async ({
      requestType,
      requestedVia,
    }: {
      requestType: BidInterestRequestType;
      requestedVia: string;
    }) => {
      const contactRequestRemark = `Requested via ${requestedVia}`;

      triggerBidInterestApi({
        propertyType,
        requestType,
        propertyListingId: propertyData?.listing_id,
        remark: contactRequestRemark,
        userMobile: resolvedMobile,
        userEmail: resolvedEmail,
        userName: resolvedName,
      });
    },
  );

  const {
    execute: handleClickViewAuthorisedOfficerButtonClick,
    isLoading: isClickViewAuthorisedOfficerButtonLoading,
    isSuccess: isClickViewAuthorisedOfficerButtonSuccess,
    isError: isClickViewAuthorisedOfficerButtonError,
    reset: resetHandleClickViewAuthorisedOfficerButtonClick,
  } = useAsyncAction(
    async ({ requestType }: { requestType: BidInterestRequestType }) => {
      triggerBidInterestApi({
        propertyType,
        requestType,
        propertyListingId: propertyData?.listing_id,
        remark: "",
        userMobile: resolvedMobile,
        userEmail: resolvedEmail,
        userName: resolvedName,
      });
      if (resolvedMobile) {
        triggerRequestContactDetailsApi({
          propertyUuid: propertyData?.uuid,
          userName: resolvedName,
          userEmail: resolvedEmail,
          userMobile: resolvedMobile,
        });
      }

      setOpenContactDetailsModal(true);
    },
  );

  const {
    execute: handleClickWhatsAppButtonClick,
    isLoading: isClickWhatsAppButtonLoading,
    isSuccess: isClickWhatsAppButtonSuccess,
    isError: isClickWhatsAppButtonError,
    reset: resetHandleClickWhatsAppButtonClick,
  } = useAsyncAction(
    async ({
      requestType,
    }: {
      closeModal?: boolean;
      requestType: BidInterestRequestType;
    }) => {
      triggerBidInterestApi({
        propertyType,
        requestType,
        propertyListingId: propertyData?.listing_id,
        remark: "",
        userMobile: resolvedMobile,
        userEmail: resolvedEmail,
        userName: resolvedName,
      });
      triggerRequestContactDetailsApi({
        propertyUuid: propertyData?.uuid,
        userName: resolvedName,
        userEmail: resolvedEmail,
        userMobile: resolvedMobile,
      });
    },
  );

  const resetCollectedMobileNumber = () => {
    setAskUserMissingDetailsModalInitiatedFrom("na");
    setMissingDetails({ mobile: "" });
  };

  const handleSubmitMobile = () => {
    if (askUserDetailsModalInitiatedFrom === "na") return;

    const sendPropertyAuthorisedOfficerOrSellerDetailsToWhatsApp = new Set([
      "send_auction_property_authorised_officer_details_to_whatsapp",
      "send_private_property_seller_details_to_whatsapp",
    ]);

    const requestAuthorisedOfficerOrSellerDetails = new Set([
      "auction_property_authorised_officer_contact_details_requested",
      "private_property_seller_contact_details_requested",
    ]);

    if (
      sendPropertyAuthorisedOfficerOrSellerDetailsToWhatsApp.has(
        askUserDetailsModalInitiatedFrom,
      )
    ) {
      handleClickWhatsAppButtonClick({
        requestType: askUserDetailsModalInitiatedFrom,
      });
      return;
    }
    if (
      requestAuthorisedOfficerOrSellerDetails.has(
        askUserDetailsModalInitiatedFrom,
      )
    ) {
      handleRequestContactDetailsButtonClick({
        requestType:
          "auction_property_authorised_officer_contact_details_requested",
        requestedVia: requestCallbackVia,
      });
      resetCollectedMobileNumber();
    }
  };

  const handleCloseContactDetailsModal = () => {
    setOpenContactDetailsModal(false);
    resetHandleClickViewAuthorisedOfficerButtonClick?.();
    resetHandleRequestContactDetailsButtonClick?.();
    resetHandleClickWhatsAppButtonClick?.();
    resetCollectedMobileNumber();
  };

  const handleCloseAskUserMissingDetailModal = () => {
    setOpenMissingDetailsModal(false);
    setAskUserMissingDetailsModalInitiatedFrom("na");
  };

  const authorisedOfficerDetailsPopupMainTitle = useMemo(() => {
    if (propertyType === "auction_property") {
      return "Authorised Officer Details";
    }
    if (propertyType === "private_property") {
      return "Seller Details";
    }
  }, [propertyType]);

  return (
    <Card className="bg-white shadow-card-shadow p-4 border-none rounded-[10px]">
      <Button
        disabled={isClickViewAuthorisedOfficerButtonLoading}
        onClick={() => {
          withAuth(() => {
            if (propertyType === "auction_property") {
              handleClickViewAuthorisedOfficerButtonClick({
                requestType: resolvedMobile
                  ? "view_auction_property_authorised_officer_details_with_send_details_to_whatsapp"
                  : "view_auction_property_authorised_officer_details",
              });
            } else if (propertyType === "private_property") {
              handleClickViewAuthorisedOfficerButtonClick({
                requestType: resolvedMobile
                  ? "view_private_property_seller_details_with_send_details_to_whatsapp"
                  : "view_private_property_seller_details",
              });
            }
          })();
        }}
        className="bg-transparent hover:bg-transparent shadow-none border border-brand-darkBlueVariant1"
      >
        <p className="flex items-center font-semibold text-brand-darkBlueVariant1">
          {title}
          {isClickViewAuthorisedOfficerButtonLoading && "..."}
          {isClickViewAuthorisedOfficerButtonSuccess &&
            !isClickViewAuthorisedOfficerButtonLoading && (
              <span className="ml-2 text-sm font-normal text-green-600">
                {"✔️"}
              </span>
            )}
          {isClickViewAuthorisedOfficerButtonError &&
            !isClickViewAuthorisedOfficerButtonLoading && (
              <span className="ml-2 text-sm font-normal text-red-600">
                {"❌"}
              </span>
            )}
        </p>
      </Button>

      <PDViewAuthorisedOfficerPopupModal
        title={authorisedOfficerDetailsPopupMainTitle}
        open={openContactDetailsModal}
        contactDetails={propertyAuthorisedOfficerContactDetails}
        setRequestCallbackVia={setRequestCallbackVia}
        onRequestContactDetailsButtonClick={() => {
          withAuth(() => {
            if (propertyType === "auction_property") {
              handleOpenCollectMissingDetailsPopup(
                "auction_property_authorised_officer_contact_details_requested",
              );
            } else if (propertyType === "private_property") {
              handleOpenCollectMissingDetailsPopup(
                "private_property_seller_contact_details_requested",
              );
            }
          })();
        }}
        showWhatsAppButton={Boolean(resolvedMobile) === false}
        onWhatsAppButtonClick={() => {
          withAuth(() => {
            if (propertyType === "auction_property") {
              handleOpenCollectMissingDetailsPopup(
                "send_auction_property_authorised_officer_details_to_whatsapp",
              );
            } else if (propertyType === "private_property") {
              handleOpenCollectMissingDetailsPopup(
                "send_private_property_seller_details_to_whatsapp",
              );
            }
          })();
        }}
        requestCallbackButtonStates={{
          processing: isRequestContactDetailsButtonLoading,
          error: isRequestContactDetailsButtonError,
          success: isRequestContactDetailsButtonSuccess,
        }}
        whatsAppButtonStates={{
          processing: isClickWhatsAppButtonLoading,
          error: isClickWhatsAppButtonError,
          success: isClickWhatsAppButtonSuccess,
        }}
        onClose={handleCloseContactDetailsModal}
      />

      <PDAskUserMissingDetailsModal
        open={openMissingDetailsModal}
        onClose={handleCloseAskUserMissingDetailModal}
        config={{
          fields: missingFields,
          title: "Complete your details",
          description: "These details are required to continue.",
        }}
        values={missingDetails}
        setValue={(field, value) =>
          setMissingDetails((prev) => ({ ...prev, [field]: value }))
        }
        onSubmit={() => {
          setOpenMissingDetailsModal(false);
          handleSubmitMobile();
        }}
      />
    </Card>
  );
};
